import { CHURCH_SOURCES } from './churchSources';
import { CARE_SOURCES } from './careSources';

export interface HistoricalSource {
  readonly url: string;
  readonly title: string;
  readonly publisher: string;
  readonly note: Readonly<{ en: string; nl: string }>;
}

export const CITY_SOURCES = {
  ...CHURCH_SOURCES,
  ...CARE_SOURCES,
  map: {
    url: 'https://www.amsterdam.nl/nieuws/achtergrond/eerste-kaart/',
    title: 'De geheimzinnige eerste kaart van Amsterdam',
    publisher: 'Gemeente Amsterdam',
    note: {
      en: 'Cornelis Anthonisz painted Amsterdam in 1538, looking down from above the IJ. His painting shows the city walls, harbour, churches and medieval town hall. It is our starting point for reconstructing the city.',
      nl: 'Cornelis Anthonisz schilderde Amsterdam in 1538, gezien vanuit de lucht boven het IJ. Zijn schilderij toont de stadsmuren, haven, kerken en het middeleeuwse stadhuis. Het is ons vertrekpunt voor het reconstrueren van de stad.',
    },
  },
  dam: {
    url: 'https://www.amsterdam.nl/stadsarchief/canon-amsterdam/1250-dam/',
    title: '1250 De Dam',
    publisher: 'Stadsarchief Amsterdam',
    note: {
      en: 'A dam joined the small settlements on opposite banks of the Amstel in the thirteenth century. As its top grew wider, it became a meeting place at the heart of the growing town.',
      nl: 'Een dam verbond in de dertiende eeuw de kleine nederzettingen aan weerszijden van de Amstel. De bovenkant werd steeds breder en groeide uit tot een ontmoetingsplek in het hart van de stad.',
    },
  },
  sluice: {
    url: 'https://www.amsterdam.nl/nieuws/achtergrond/dam-centrum-wereld/',
    title: 'De Dam: Het centrum van de wereld',
    publisher: 'Gemeente Amsterdam',
    note: {
      en: 'The medieval Dam had a sluice for boats and a fish market beside it. The neighbouring square, Die Plaetse, held the town hall. Water, trade and city government came together here.',
      nl: 'Op de middeleeuwse Dam lag een sluis voor boten met een vismarkt ernaast. Op het aangrenzende plein, Die Plaetse, stond het stadhuis. Water, handel en stadsbestuur kwamen hier samen.',
    },
  },
  fish: {
    url: 'https://www.amsterdam.nl/stadsarchief/stukken/dieren/vismarkt/',
    title: 'Vismarkt',
    publisher: 'Stadsarchief Amsterdam',
    note: {
      en: 'Fish was sold on the Dam from medieval times, at covered selling benches. Sea fish was traded on the IJ side and river fish on the Rokin side. The article’s illustration is from a later century.',
      nl: 'Al in de middeleeuwen werd op de Dam vis verkocht, aan overdekte toonbanken. Zeevis lag aan de kant van het IJ en riviervis aan de kant van het Rokin. De afbeelding bij het artikel komt uit een latere eeuw.',
    },
  },
  harbour: {
    url: 'https://www.amsterdam.nl/nieuws/achtergrond/damrak-kiem-wereldstad/',
    title: 'Het Damrak: De kiem van een wereldstad',
    publisher: 'Gemeente Amsterdam',
    note: {
      en: 'By around 1400, the Damrak was a busy part of the harbour. Large seagoing ships moored at the IJ; smaller inland boats used the Damrak. Warehouses behind Warmoesstraat opened directly onto the water.',
      nl: 'Rond 1400 was het Damrak een druk deel van de haven. Grote zeeschepen legden aan bij het IJ; kleinere binnenvaartboten gebruikten het Damrak. Pakhuizen achter de Warmoesstraat kwamen direct op het water uit.',
    },
  },
  bridge: {
    url: 'https://www.amsterdam.nl/stadsarchief/stukken/verkeer/nieuwe-brug/',
    title: 'Nieuwe Brug',
    publisher: 'Stadsarchief Amsterdam',
    note: {
      en: 'Goods reached the Damrak warehouses by boat. The western quay was much narrower than today. In 1538 the Nieuwe Brug was still wooden; the stone bridge described in the article came later, in 1561.',
      nl: 'Boten brachten goederen naar de pakhuizen aan het Damrak. De westelijke kade was veel smaller dan nu. In 1538 was de Nieuwe Brug nog van hout; de stenen brug uit het artikel kwam later, in 1561.',
    },
  },
} as const satisfies Readonly<Record<string, HistoricalSource>>;

export const DUTCH_SOURCE_NOTES: Readonly<Record<string, string>> = Object.fromEntries(
  Object.values(CITY_SOURCES).map((source) => [source.note.en, source.note.nl])
);

export function historicalSource(url: string): HistoricalSource {
  const source = Object.values(CITY_SOURCES).find((source) => source.url === url);
  if (!source) throw new Error('Unknown historical source');
  return source;
}
